import { CircularProgress, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { BaseModal } from "../../BaseModal";
import { CustomButton } from "../../CustomButton";
import { DotIcon } from "../../../images";
import { setIsNotificationsOpen } from "../../../../features/companySlice";
import { useAppDispatch } from "../../../../hooks";
import {
  accountsApi,
  campaignsApi,
  useGenerateAccountsPlanListMutation,
} from "../../../../api";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { ManageSectionsBlock } from "../../ManageSectionsBlock";
import {
  ManageSectionsModals,
  ModalSize,
  PlaybookPlanNames,
} from "../../../../types/enums";
import { AccountPlanItem } from "../../../../types/apiTypes";
import { ModalListWithSelect } from "../../ModalListWithSelect";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  accounts: AccountPlanItem[];
  campaign_id: string;
  canLoadMore: boolean;
  isLoading: boolean;
  page: number;
  loadMore: () => void;
  setSearchHandler: (search: string) => void;
  startPage: number;
};

const ListItem = ({ text }: { text: string }) => {
  return (
    <Stack flexDirection="row" alignItems="flex-start" paddingLeft="4px">
      <DotIcon
        width="20px"
        height="20px"
        color="#464646"
        style={{ marginTop: "3px" }}
      />
      <Typography variant="subtitle1" color="text.secondary">
        {text}
      </Typography>
    </Stack>
  );
};

const textList = [
  "All buying committee is verified and meet your expectations",
  "The search for additional contacts is completed based on gap analysis results",
  'Contact information is updated using "Validate Contacts Data" feature',
];

export const TargetedAccountPlansModal = ({
  isOpen,
  onClose,
  accounts,
  campaign_id,
  canLoadMore = false,
  isLoading = false,
  page,
  loadMore,
  setSearchHandler,
  startPage,
}: Props) => {
  const dispatch = useAppDispatch();
  const [selected, setSelected] = useState<string[]>([]);
  const [isHide, setIsHide] = useState<boolean>(false);
  const [isSelectedAll, setIsSelectedAll] = useState<boolean>(true);

  const [generateAccountsPlanList, { isLoading: isGeneratingAccountPlan }] =
    useGenerateAccountsPlanListMutation();

  const submitHandler = () => {
    generateAccountsPlanList({
      campaign_id,
      account_ids: selected,
      include_all: isSelectedAll,
    })
      .unwrap()
      .then(() => {
        dispatch(setIsNotificationsOpen(true));
        onClose();
        dispatch(accountsApi.util.invalidateTags(["list-view"]));
        dispatch(campaignsApi.util.invalidateTags(["activities", "summary"]));
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type="error"
            title={`Failed to Start Generating ${PlaybookPlanNames.account_plans}.`}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
          />,
        );
      });
  };

  const prepareAccounts = useMemo(
    () =>
      accounts.map(({ account_id, account_name, status }) => ({
        id: account_id,
        name: account_name,
        missed: 0,
        status,
      })),
    [accounts],
  );

  return (
    <BaseModal
      open={isOpen}
      onClose={onClose}
      title={`Get Targeted ${PlaybookPlanNames.account_plans}`}
      sx={{
        padding: "32px",
        maxWidth: ModalSize.mid,
        width: "94%",
        visibility: isHide ? "hidden" : "visible",
      }}
      btnBlock={
        <Stack direction="row" gap="8px" paddingTop="16px">
          <CustomButton
            onClick={onClose}
            variant="contained"
            textColor="#4C545B"
            fullWidth
            type="button"
            customColor="#F1F1F1"
            disabled={isGeneratingAccountPlan}
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={submitHandler}
            variant="contained"
            fullWidth
            type="button"
            customColor="#1C3C6C"
            startIcon={
              isGeneratingAccountPlan && (
                <CircularProgress size={24} sx={{ color: "inherit" }} />
              )
            }
            disabled={isGeneratingAccountPlan || selected.length === 0}
          >
            Get {PlaybookPlanNames.account_plans}
          </CustomButton>
        </Stack>
      }
    >
      <Stack gap="16px" paddingBottom="12px">
        <Stack>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            fontWeight={500}
          >
            Which accounts would you like to create{" "}
            {PlaybookPlanNames.account_plans} for?
          </Typography>
          {accounts && (
            <ModalListWithSelect
              page={page}
              loadMore={loadMore}
              isLoading={isLoading}
              canLoadMore={canLoadMore}
              itemsList={prepareAccounts}
              withSelectAll
              withHeader={false}
              setIsSelectedAll={setIsSelectedAll}
              setSelectedHandler={setSelected}
              setSearchHandler={setSearchHandler}
              startPage={startPage}
            />
          )}
        </Stack>
        <Stack>
          <Typography variant="subtitle1" color="text.secondary">
            {PlaybookPlanNames.account_plans}{" "}can only be generated once.
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Please make sure everything is correct before proceeding:
          </Typography>
        </Stack>
        <Stack>
          {textList.map((text, index) => (
            <ListItem key={index} text={text} />
          ))}
        </Stack>
      </Stack>
      <ManageSectionsBlock
        adjustSection={PlaybookPlanNames.account_plan}
        manageSections={PlaybookPlanNames.account_plan}
        hidePrevModalHandler={setIsHide}
        closePrevModalHandler={onClose}
        modal={ManageSectionsModals.account_plan}
      />
    </BaseModal>
  );
};
